import type { Network, Psbt } from 'bitcoinjs-lib';
import { address } from 'bitcoinjs-lib';

import { InvalidAmountError } from '../../../errors/InvalidAmountError';
import { DUST_LIMIT, SATOSHIS_PER_BTC } from '../../constants';

type AddOutputsToPsbtOptions = {
  accountAddress: string;
  amountInSatoshis: number;
  changeAmount: number;
  hasChangeOutput: boolean;
  network: Network;
  psbt: Psbt;
  recipientAddress: string;
};

/**
 * Adds outputs to PSBT (recipient and optionally change)
 *
 * @param options.accountAddress - The sender's address for receiving change
 * @param options.amountInSatoshis - Amount to send to the recipient in satoshis
 * @param options.changeAmount - Amount to return as change in satoshis
 * @param options.hasChangeOutput - Whether to include a change output
 * @param options.network - Bitcoin network configuration (mainnet or testnet)
 * @param options.psbt - The PSBT instance to add outputs to
 * @param options.recipientAddress - The recipient's Bitcoin address
 */
export const addOutputsToPsbt = (options: AddOutputsToPsbtOptions): void => {
  const {
    accountAddress,
    amountInSatoshis,
    changeAmount,
    hasChangeOutput,
    network,
    psbt,
    recipientAddress,
  } = options;

  if (amountInSatoshis < DUST_LIMIT) {
    throw new InvalidAmountError({
      amountInSatoshis,
      reason: `Amount is below dust limit of ${DUST_LIMIT} satoshis (${DUST_LIMIT / SATOSHIS_PER_BTC} BTC)`,
    });
  }

  psbt.addOutput({
    script: address.toOutputScript(recipientAddress, network),
    value: BigInt(amountInSatoshis),
  });

  if (hasChangeOutput) {
    psbt.addOutput({
      script: address.toOutputScript(accountAddress, network),
      value: BigInt(changeAmount),
    });
  }
};
